"use strict";
/**
 * Browser Push Notifications
 *
 * Manages browser push notifications for SkySign.
 * Requires user permission to display native browser notifications.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.requestPermissionOnLogin = exports.showDocumentSignedNotification = exports.showNewDocumentNotification = exports.showBrowserNotification = exports.hasNotificationPermission = exports.requestNotificationPermission = exports.getNotificationPermission = exports.isNotificationSupported = void 0;
const NOTIFICATION_PERMISSION_KEY = "skysign_notification_permission";
/**
 * Check if browser supports notifications
 */
const isNotificationSupported = () => {
    return "Notification" in window;
};
exports.isNotificationSupported = isNotificationSupported;
/**
 * Get current notification permission status
 */
const getNotificationPermission = () => {
    if (!(0, exports.isNotificationSupported)()) {
        return "denied";
    }
    return Notification.permission;
};
exports.getNotificationPermission = getNotificationPermission;
/**
 * Request notification permission from user
 */
const requestNotificationPermission = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!(0, exports.isNotificationSupported)()) {
        console.warn("Browser does not support notifications");
        return "denied";
    }
    try {
        const permission = yield Notification.requestPermission();
        localStorage.setItem(NOTIFICATION_PERMISSION_KEY, permission);
        return permission;
    }
    catch (error) {
        console.error("Failed to request notification permission:", error);
        return "denied";
    }
});
exports.requestNotificationPermission = requestNotificationPermission;
/**
 * Check if user has granted notification permission
 */
const hasNotificationPermission = () => {
    return (0, exports.getNotificationPermission)() === "granted";
};
exports.hasNotificationPermission = hasNotificationPermission;
/**
 * Show browser push notification
 */
const showBrowserNotification = (title, options) => {
    if (!(0, exports.hasNotificationPermission)()) {
        console.warn("Notification permission not granted");
        return null;
    }
    // Check if document is visible (don't show notification if app is in focus)
    if (document.visibilityState === "visible") {
        return null;
    }
    try {
        const _a = options || {}, { onClick } = _a, notificationOptions = __rest(_a, ["onClick"]);
        const notification = new Notification(title, Object.assign({ icon: "/favicon.ico", badge: "/favicon.ico", requireInteraction: false }, notificationOptions));
        // Handle click event
        if (onClick) {
            notification.onclick = () => {
                window.focus();
                onClick();
                notification.close();
            };
        }
        else {
            // Default behavior: focus window
            notification.onclick = () => {
                window.focus();
                notification.close();
            };
        }
        // Auto-close after 10 seconds
        setTimeout(() => {
            notification.close();
        }, 10000);
        return notification;
    }
    catch (error) {
        console.error("Failed to show browser notification:", error);
        return null;
    }
};
exports.showBrowserNotification = showBrowserNotification;
/**
 * Show new document notification
 */
const showNewDocumentNotification = (senderName, documentTitle, onNavigate) => {
    return (0, exports.showBrowserNotification)("New Document to Sign", {
        body: `${senderName} sent you "${documentTitle}"`,
        tag: "new-document",
        icon: "/favicon.ico",
        onClick: onNavigate,
    });
};
exports.showNewDocumentNotification = showNewDocumentNotification;
/**
 * Show document signed notification
 */
const showDocumentSignedNotification = (documentTitle) => {
    return (0, exports.showBrowserNotification)("Document Signed", {
        body: `"${documentTitle}" has been signed successfully`,
        tag: "document-signed",
        icon: "/favicon.ico",
    });
};
exports.showDocumentSignedNotification = showDocumentSignedNotification;
/**
 * Request permission on user login (if not already set)
 */
const requestPermissionOnLogin = () => __awaiter(void 0, void 0, void 0, function* () {
    const currentPermission = (0, exports.getNotificationPermission)();
    // Only ask if permission is in default state (not granted or denied)
    if (currentPermission === "default") {
        yield (0, exports.requestNotificationPermission)();
    }
});
exports.requestPermissionOnLogin = requestPermissionOnLogin;
exports.default = {
    isSupported: exports.isNotificationSupported,
    getPermission: exports.getNotificationPermission,
    requestPermission: exports.requestNotificationPermission,
    hasPermission: exports.hasNotificationPermission,
    show: exports.showBrowserNotification,
    newDocument: exports.showNewDocumentNotification,
    documentSigned: exports.showDocumentSignedNotification,
    requestOnLogin: exports.requestPermissionOnLogin,
};
